/**
 * More examples.
 *
 * /how-it-works carries one interactive example, because a reader deciding whether to
 * trust a claim wants one thing they can operate — not four diagrams in a row. This is
 * where the other three went, for the reader who came looking for them: the call path,
 * the cost of indexing a real repository, and the privacy boundary.
 *
 * It is a second-click page and it is written like one: no pricing argument, no metric
 * band, one CTA at the end. The same two labels hold the copy honest as everywhere else
 * — **measured** figures come from the published corpus, **illustrative** animations use
 * the real sequence with round per-step numbers.
 *
 * `#boundary` is a real anchor: /how-it-works links straight to the last section, so a
 * reader who followed the privacy thread lands on the diagram rather than at the top of
 * a page they then have to scan.
 */

import { Link } from 'react-router-dom'

import { Footer, Masthead } from '@/shared/ui/Chrome'
import { Label } from '@/shared/ui/primitives'
import { CompilePipeline, FlowMap, PrivacyBoundary } from '@/shared/ui/walkthrough'

export function Examples() {
  return (
    <>
      <Masthead />
      <main id="main">
        <section className="shell" style={{ paddingBlock: 'var(--s8) var(--s6)' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)', maxWidth: '54rem' }}>
            <Label index="00">Examples</Label>
            <h1 style={{ fontSize: 'clamp(2.2rem, 5vw, 3.4rem)' }}>
              Three more things you can <span className="accent-text">press play on</span>.
            </h1>
            <p className="lead" style={{ maxWidth: '54ch' }}>
              Where a question goes from your agent to an answer, what it costs to index a
              real repository, and exactly what crosses the line between your machine and
              our servers.
            </p>
            <div className="row" style={{ gap: 'var(--s3)', flexWrap: 'wrap' }}>
              <Link to="/docs/install" className="cta cta--primary">
                Install free →
              </Link>
              <Link to="/how-it-works" className="cta">
                ← Back to the main example
              </Link>
            </div>
            <p className="mono-note" style={{ maxWidth: '80ch' }}>
              Everything here runs in your browser. Nothing is uploaded, no repository is
              read, and no API is called.
            </p>
          </div>
        </section>

        <section className="shell" style={{ paddingBottom: 'var(--s7)' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
              <Label index="01">The call path</Label>
              <h2 style={{ maxWidth: '30ch' }}>One question, end to end</h2>
              <p className="dim" style={{ maxWidth: '68ch' }}>
                Archis runs as a local server your coding agent talks to. Indexing is not a step
                you have to remember: before answering, Archis compares the working tree with its
                index, so a repository nobody indexed still works and a teammate&apos;s changes
                are picked up on the next call.
              </p>
            </div>
            <FlowMap />
          </div>
        </section>

        <section className="shell" style={{ paddingBottom: 'var(--s7)' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
              <Label index="02">Indexing a real repository</Label>
              <h2 style={{ maxWidth: '30ch' }}>Read once. Reuse what it means.</h2>
              <p className="dim" style={{ maxWidth: '68ch' }}>
                Five stages turn source into a compact form a model reads in a fraction of the
                tokens. The repositories in the picker are public and pinned to released tags,
                so every figure below is reproducible on your own checkout. It is one-way by
                design — the original file is always kept, which is exactly why the compact
                form is free to drop formatting and boilerplate.
              </p>
            </div>
            <CompilePipeline />
          </div>
        </section>

        <section
          className="shell"
          id="boundary"
          style={{ paddingBottom: 'var(--s8)', scrollMarginTop: '5rem' }}
        >
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
              <Label index="03">The boundary</Label>
              <h2 style={{ maxWidth: '30ch' }}>What crosses, and what cannot</h2>
              <p className="dim" style={{ maxWidth: '68ch' }}>
                Everything semantic happens on your machine. Everything commercial happens in
                the cloud. Watch the third packet: source code has nowhere to arrive, because
                there is no endpoint on our side that accepts it.
              </p>
            </div>
            <PrivacyBoundary />
          </div>
        </section>

        <section className="shell" style={{ paddingBottom: 'var(--s9)' }}>
          <div className="panel" style={{ textAlign: 'center', padding: 'var(--s7) var(--s5)' }}>
            <h2 style={{ maxWidth: '24ch', margin: '0 auto' }}>
              Now run it on your own repository
            </h2>
            <p className="dim" style={{ maxWidth: '52ch', margin: 'var(--s4) auto var(--s5)' }}>
              <code>chi measure ./src</code> reproduces the reduction on your code in a
              minute, and Archis Free is the whole local engine with no card.
            </p>
            <div className="row" style={{ gap: 'var(--s3)', justifyContent: 'center' }}>
              <Link to="/docs/install" className="cta cta--primary">
                Install free →
              </Link>
              <Link to="/privacy#telemetry" className="cta">
                Read the privacy disclosure
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}
